export type TenancyView = 'children' | 'loader' | 'error';

export interface ResolveTenancyViewInput {
  /** The route is role-gated (its page declares an `accessFor` list). */
  isProtectedPage: boolean;
  /**
   * The user machine has reached a settled auth state — the profile fetch has
   * finished, either successfully (`loggedIn.loaded`, roles known) or with an
   * error (`loggedIn.error`, roles won't arrive). In both cases we know as much
   * as we ever will, so the tenancy check can run. `false` means auth is still
   * transitioning: bootstrapping a login (`loggedIn.loading`) or being signed
   * out (`loggedOut`), where roles are unreliable and must not be enforced.
   */
  isUserSettled: boolean;
  /** The loaded user holds one of the roles required by the route. */
  haveAccess: boolean;
}

/**
 * Decide what a tenancy-gated route renders. Splitting this out keeps the
 * "never flash 'not authorized' while auth is transitioning" rule pure and
 * unit-testable (Manager's jsdom test env can't render the web-component UI).
 */
export const resolveTenancyView = ({
  isProtectedPage,
  isUserSettled,
  haveAccess,
}: ResolveTenancyViewInput): TenancyView => {
  // Public (non-gated) routes always render their content.
  if (!isProtectedPage) {
    return 'children';
  }

  // Auth state hasn't settled: the user is either bootstrapping a login or
  // being signed out. Roles are unreliable here (ctx.user may be null or the
  // profile not yet fetched), so never flash the "not authorized" screen —
  // show a neutral loader and let the redirect / role fetch settle.
  if (!isUserSettled) {
    return 'loader';
  }

  // Auth has settled (profile loaded, or failed to load): enforce the tenancy
  // check. A lacking role — including the case where the profile fetch errored
  // and no roles arrived — shows the error screen (with a way out), never an
  // endless loader.
  return haveAccess ? 'children' : 'error';
};
